import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, CurrencyDollarIcon, PresentationChartLineIcon, SparklesIcon, GlobeEuropeAfricaIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const PostHarvest = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const harvestSolutions = [
        "Scientific harvesting techniques",
        "Cleaning, grading & sorting systems",
        "Moisture & quality management",
        "Storage optimization solutions",
        "Controlled environment recommendations",
        "Market linkage & value enhancement planning"
    ];

    const valueStrategies = [
        { title: "Quality grading & branding", icon: SparklesIcon },
        { title: "Primary processing solutions", icon: BeakerIcon },
        { title: "Packaging innovation", icon: ShieldCheckIcon },
        { title: "Shelf-life improvement techniques", icon: GlobeEuropeAfricaIcon },
        { title: "Market-oriented product positioning", icon: PresentationChartLineIcon }
    ];

    const whyItMatters = [
        "Reduces quantitative & qualitative losses",
        "Preserves nutritional value",
        "Improves export potential",
        "Increases farmer income",
        "Strengthens supply chain efficiency"
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src="https://images.unsplash.com/photo-1606041008023-472dfb5e530f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1736&q=80"
                        alt="Post-Harvest & Value Addition"
                        className="w-full h-full object-cover opacity-80"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                    >
                        Post-Harvest & <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">Value Addition</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-gray-200 font-light"
                    >
                        Protecting Harvest. Increasing Profit.
                    </motion.p>
                </div>
            </section>

            <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-1/2"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            At <strong className="text-gray-900 font-bold">Marvelous Agri Solution</strong>, we believe the real profit begins after harvest.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light">
                            Post-Harvest & Value Addition focuses on reducing losses, preserving quality, and enhancing market value through scientific handling, storage, processing, and smart marketing strategies.
                        </p>

                        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl mb-12">
                            <p className="text-xl font-medium text-emerald-900 italic">
                                Because production is important — but preservation creates profitability.
                            </p>
                        </div>

                        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                            Our Post-Harvest Solutions
                        </h3>

                        <div className="space-y-4 mb-8">
                            {harvestSolutions.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-white shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all">
                                    <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-800 font-medium text-lg">{item}</span>
                                </div>
                            ))}
                        </div>

                        <p className="text-emerald-800 font-medium italic mb-10 border-b border-gray-200 pb-6">
                            Every step is designed to minimize loss and maximize return.
                        </p>

                        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                            Why Post-Harvest Management Matters
                        </h3>

                        <div className="mt-8 space-y-4">
                            {whyItMatters.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                                    <CheckCircleIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                    <span className="text-gray-700 font-medium">{item}</span>
                                </div>
                            ))}
                        </div>

                    </motion.div>

                    <div className="lg:w-1/2 relative space-y-12">

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">
                                Value Addition Strategies
                            </h3>

                            <p className="text-gray-600 mb-8 font-light">
                                We help transform raw produce into higher-value products through:
                            </p>

                            <div className="space-y-4 mb-8">
                                {valueStrategies.map((strategy, idx) => (
                                    <div key={idx} className="flex items-center gap-4 p-3 hover:bg-emerald-50 rounded-xl transition-colors">
                                        <div className="bg-emerald-100 p-3 rounded-xl flex-shrink-0">
                                            <strategy.icon className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <p className="text-gray-800 font-medium text-lg">{strategy.title}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-gray-50 border-t-2 border-emerald-500 p-6 rounded-b-xl -mx-8 -mb-8 mt-8">
                                <p className="font-bold text-gray-900 mb-2">The result?</p>
                                <p className="font-medium text-emerald-700 italic">Higher price realization.</p>
                                <p className="font-medium text-emerald-700 italic">Better shelf stability.</p>
                                <p className="font-medium text-emerald-700 italic">Stronger market competitiveness.</p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="bg-gray-900 p-10 rounded-3xl shadow-2xl text-white relative overflow-hidden"
                        >
                            <div className="absolute -right-20 -top-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl text-white"></div>

                            <h3 className="text-3xl font-bold mb-8 flex items-center gap-3 relative z-10 text-emerald-400">
                                From Harvest to Higher Value
                            </h3>

                            <div className="relative z-10 space-y-4 border-l-4 border-emerald-500 pl-6 py-2">
                                <p className="text-xl font-light text-gray-300">
                                    We convert crops into <span className="font-medium text-white">premium products</span>.
                                </p>
                                <p className="text-xl font-light text-gray-300">
                                    We convert storage into <span className="font-medium text-white">strategy</span>.
                                </p>
                                <p className="text-xl font-light text-gray-300">
                                    We convert yield into <span className="font-medium text-white">wealth</span>.
                                </p>
                            </div>

                        </motion.div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default PostHarvest;
